import { ProposalSlot, ProposalSlotBox } from "../types";

// Boxes whose tops are this close, in PDF points, to the first box of a line
// sit on that line of the page and are read left to right.
const SAME_LINE_POINTS = 4;

interface Placed {
  slot: ProposalSlot;
  box?: ProposalSlotBox;
}

// Where a slot first appears in the document: its earliest page, then its
// highest box, then its leftmost.
const firstBox = (slot: ProposalSlot) =>
  slot.boxes?.reduce<ProposalSlotBox | undefined>(
    (first, box) =>
      !first ||
      box.page < first.page ||
      (box.page === first.page &&
        (box.y < first.y || (box.y === first.y && box.x < first.x)))
        ? box
        : first,
    undefined
  );

// Each box's line on its page. Lines are formed top to bottom, so a box joins
// a line only within reach of that line's first box, and a chain of near
// boxes can't merge rows the page keeps apart.
const lineIndexes = (placed: Placed[]) => {
  const lines = new Map<Placed, number>();
  const byPosition = placed
    .filter((each) => each.box)
    .sort(
      (left, right) =>
        left.box!.page - right.box!.page || left.box!.y - right.box!.y
    );
  let line = -1;
  let lineTop: ProposalSlotBox | undefined;
  byPosition.forEach((each) => {
    const box = each.box!;
    if (
      !lineTop ||
      box.page !== lineTop.page ||
      box.y - lineTop.y > SAME_LINE_POINTS
    ) {
      line += 1;
      lineTop = box;
    }
    lines.set(each, line);
  });
  return lines;
};

// The slots as the document reads, page by page, top to bottom and left to
// right, so the fields follow the preview beside them. A slot with no box keeps
// its place after the rest. A flowing template has no boxes; its document
// gives `reading`, the slot ids in reading order.
export const inDocumentOrder = (
  slots: ProposalSlot[],
  reading?: string[]
): ProposalSlot[] => {
  if (reading) {
    const place = (slot: ProposalSlot) => {
      const index = reading.indexOf(slot.id);
      return index === -1 ? reading.length : index;
    };
    return [...slots].sort((left, right) => place(left) - place(right));
  }
  const placed: Placed[] = slots.map((slot) => ({
    slot,
    box: firstBox(slot)
  }));
  const lines = lineIndexes(placed);
  return [...placed]
    .sort((left, right) => {
      if (!left.box || !right.box) {
        return (left.box ? 0 : 1) - (right.box ? 0 : 1);
      }
      return lines.get(left)! - lines.get(right)! || left.box.x - right.box.x;
    })
    .map(({ slot }) => slot);
};
